import { datadogLogs } from '@datadog/browser-logs';
import { useNavigate } from 'react-router-dom';
import { showToast } from 'components/Toast';
import { useAppDispatch } from 'helpers/hooks';
import { useAdminAuth } from 'hooks/useAdminAuth';
import {
  useActivateShoppingCartMutation,
  useUpdateShoppingCartMutation,
} from 'services/shoppingCartApi';
import {
  AdminShoppingCart,
  AdminShoppingCartResponse,
  RecipientSelectionMode,
} from 'store/shoppingCart/types';
import { uploadCheckoutPageImage } from './uploadCheckoutPageImage';
import { buildActivatePayload, buildUpdatePayload } from './formValues';
import { ShoppingCartFormValues } from './types';
import { trackEvent } from 'helpers/mixpanelEvents';

type MutationError = { status?: number; data?: { detail?: string } };

const INVALID_DATA_TOAST = 'Invalid data. Please check the form and try again.';

export const useShoppingCartSubmit = (cart: AdminShoppingCart | null | undefined) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { clientId, activeRole } = useAdminAuth();
  const [activateShoppingCart] = useActivateShoppingCartMutation();
  const [updateShoppingCart] = useUpdateShoppingCartMutation();

  // Resolves false (after logging) instead of throwing, so the caller can report a
  // partial save without losing the cart write that already succeeded.
  const attachCheckoutImage = (file: File, pageId: number | undefined) =>
    (pageId
      ? uploadCheckoutPageImage(dispatch, file, pageId, activeRole ?? '')
          // Flag set only after the filename is recorded, so a failed upload never
          // leaves the donor page pointing at a missing image.
          .then(() =>
            updateShoppingCart({ clientId: clientId ?? 0, body: { has_image: true } }).unwrap()
          )
          .then(() => true)
      : Promise.reject(new Error('Shopping cart response has no page id'))
    ).catch(error => {
      datadogLogs.logger.error(
        'Failed to upload shopping cart checkout page image',
        { clientId, pageId },
        error as Error
      );
      return false;
    });

  const handleActivate = async (values: ShoppingCartFormValues) => {
    let activatedCart: AdminShoppingCartResponse;
    try {
      activatedCart = await activateShoppingCart({
        clientId: clientId ?? 0,
        body: buildActivatePayload(values),
      }).unwrap();
    } catch (error) {
      const err = error as MutationError;
      if (err?.status === 409 || err?.status === 400) {
        showToast(err.data?.detail ?? 'Could not activate this shopping cart.', 'error');
      } else if (err?.status === 422) {
        showToast(INVALID_DATA_TOAST, 'error');
      } else {
        datadogLogs.logger.error(
          'Failed to activate shopping cart',
          { status: err?.status },
          error as Error
        );
        showToast('Something went wrong. Please try again.', 'error');
      }
      return;
    }
    trackEvent('Shopping Cart Saved', {
      screen: 'shopping_cart_detail',
      interaction_type: 'activate',
    });

    if (values.selectedFile) {
      const imageUploaded = await attachCheckoutImage(
        values.selectedFile,
        activatedCart.cart?.page.page_id
      );
      if (!imageUploaded) {
        showToast('Shopping cart activated, but the image could not be uploaded.', 'error');
        navigate('/shopping_cart');
        return;
      }
    }

    showToast('Shopping cart activated successfully.', 'success');
    navigate('/shopping_cart');
  };

  const handleSave = async (values: ShoppingCartFormValues) => {
    try {
      await updateShoppingCart({
        clientId: clientId ?? 0,
        body: buildUpdatePayload(values),
      }).unwrap();
    } catch (error) {
      const err = error as MutationError;
      const detail = err.data?.detail;
      if (err?.status === 409 || err?.status === 400) {
        showToast(detail ?? 'Could not save this shopping cart.', 'error');
      } else if (err?.status === 422) {
        showToast(INVALID_DATA_TOAST, 'error');
      } else {
        datadogLogs.logger.error(
          'Failed to update shopping cart',
          { status: err?.status },
          error as Error
        );
        showToast('Something went wrong. Please try again.', 'error');
      }
      return;
    }
    trackEvent('Shopping Cart Saved', { screen: 'shopping_cart_detail', interaction_type: 'edit' });

    if (values.selectedFile) {
      const imageUploaded = await attachCheckoutImage(values.selectedFile, cart?.page.page_id);
      if (!imageUploaded) {
        showToast('Shopping cart saved, but the image could not be uploaded.', 'error');
        return;
      }
    }

    showToast('Shopping cart saved successfully.', 'success');
    navigate('/shopping_cart');
  };

  const handleSubmit = (values: ShoppingCartFormValues) => {
    if (
      values.recipient_selection_mode === RecipientSelectionMode.RECIPIENTS_LIST &&
      !values.recipients_selected?.length
    ) {
      showToast('Select at least one recipient.', 'error');
      return;
    }
    return cart ? handleSave(values) : handleActivate(values);
  };

  return handleSubmit;
};
